import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ShoppingBag, X, Plus, Minus, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";
import { useGymArticles, GymArticle } from "@/hooks/payments/useGymArticles";
import { usePaymentMethods } from "@/hooks/payments/usePaymentMethods";
import { PaymentMethodSelector } from "@/components/settings/admin/stats/payments/form/PaymentMethodSelector";
import { PaymentMethodManager } from "@/components/settings/admin/stats/payments/form/PaymentMethodManager";
import { cn } from "@/lib/utils";

interface ArticleSaleDialogProps {
  gymId: string;
}

interface CartItem {
  article: GymArticle;
  quantity: number;
  unitPrice: number;
}

interface MemberOption {
  id: string;
  name: string;
  source: "profile" | "pending";
}

export const ArticleSaleDialog = ({ gymId }: ArticleSaleDialogProps) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();
  const [open, setOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");
  const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [paidAt, setPaidAt] = useState<string>(format(new Date(), "yyyy-MM-dd"));
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);
  const [isManagingMethods, setIsManagingMethods] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { articles, isLoading: articlesLoading } = useGymArticles(gymId);
  const { methods, isLoading: methodsLoading } = usePaymentMethods(gymId);

  useEffect(() => {
    if (!selectedMethodId && methods.length > 0) {
      setSelectedMethodId(methods[0].id);
    }
  }, [methods, selectedMethodId]);

  const { data: members } = useQuery({
    queryKey: ["gym-members-for-sale", gymId],
    queryFn: async () => {
      const [profilesRes, pendingRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, first_name, last_name, username")
          .eq("gym_id", gymId),
        supabase
          .from("pending_members")
          .select("id, first_name, last_name, dni")
          .eq("gym_id", gymId),
      ]);
      const profiles: MemberOption[] = (profilesRes.data || []).map((p) => ({
        id: p.id,
        name:
          [p.first_name, p.last_name].filter(Boolean).join(" ").trim() ||
          p.username?.split("@")[0] ||
          "Sin nombre",
        source: "profile" as const,
      }));
      const pending: MemberOption[] = (pendingRes.data || [])
        .filter((p: any) => p.dni !== "__WALKIN__")
        .map((p) => ({
          id: p.id,
          name: [p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "Sin nombre",
          source: "pending" as const,
        }));
      return [...profiles, ...pending].sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!gymId && open,
  });

  const filteredMembers = useMemo(() => {
    if (!members) return [];
    const q = memberSearch.toLowerCase().trim();
    if (!q) return members.slice(0, 20);
    return members.filter((m) => m.name.toLowerCase().includes(q)).slice(0, 20);
  }, [members, memberSearch]);

  const total = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  const addArticle = (article: GymArticle) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.article.id === article.id);
      if (existing) {
        return prev.map((c) =>
          c.article.id === article.id ? { ...c, quantity: c.quantity + 1 } : c
        );
      }
      return [...prev, { article, quantity: 1, unitPrice: article.price }];
    });
  };

  const removeArticle = (id: string) =>
    setCart((prev) => prev.filter((c) => c.article.id !== id));

  const updateQuantity = (id: string, q: number) => {
    if (q <= 0) return removeArticle(id);
    setCart((prev) => prev.map((c) => (c.article.id === id ? { ...c, quantity: q } : c)));
  };

  const updateUnitPrice = (id: string, price: number) => {
    setCart((prev) =>
      prev.map((c) => (c.article.id === id ? { ...c, unitPrice: Math.max(0, price) } : c))
    );
  };

  const reset = () => {
    setSelectedMember(null);
    setMemberSearch("");
    setCart([]);
    setPaidAt(format(new Date(), "yyyy-MM-dd"));
  };

  const handleSubmit = async () => {
    if (cart.length === 0 || total <= 0) return;
    const selectedMethod = methods.find((m) => m.id === selectedMethodId);
    if (!selectedMethod) return;

    setIsSubmitting(true);
    try {
      let userId = selectedMember?.id;
      if (!userId) {
        const { data: walkinId, error: walkinErr } = await supabase.rpc(
          "get_or_create_walkin_customer" as any,
          { p_gym_id: gymId }
        );
        if (walkinErr) throw walkinErr;
        userId = walkinId as string;
      }

      const paidAtIso = new Date(paidAt + "T12:00:00").toISOString();

      // Insert one payment row per unit so each article appears individually
      // in the arqueo (and the breakdown counts units correctly).
      const rows = cart.flatMap((item) =>
        Array.from({ length: item.quantity }).map(() => ({
          user_id: userId!,
          amount: item.unitPrice,
          discount: 0,
          description: `Venta de artículo: ${item.article.name}`,
          validity_days: 0,
          paid_at: paidAtIso,
          payment_method: selectedMethod.name,
          article_id: item.article.id,
          is_article_sale: true,
        }))
      );

      const { error } = await supabase.from("payments").insert(rows);

      if (error) throw error;

      success("Venta registrada", { description: "La venta del artículo fue registrada" });
      await queryClient.invalidateQueries({ queryKey: ["payments", gymId] });
      reset();
      setOpen(false);
    } catch (e) {
      console.error(e);
      showError("No se pudo registrar la venta");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isManagingMethods) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95%] max-w-md mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
          <PaymentMethodManager gymId={gymId} onBack={() => setIsManagingMethods(false)} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <ShoppingBag className="h-4 w-4" />
          Registrar venta de artículo
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95%] max-w-lg mx-auto p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar venta de artículo</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Member selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Cliente (opcional)</label>
            {selectedMember ? (
              <div className="flex items-center justify-between p-2 border rounded-md bg-muted/40">
                <span className="text-sm">{selectedMember.name}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setSelectedMember(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Buscar miembro..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                  />
                </div>
                {memberSearch && (
                  <div className="border rounded-md max-h-40 overflow-y-auto">
                    {filteredMembers.length === 0 ? (
                      <p className="p-2 text-xs text-muted-foreground">Sin resultados</p>
                    ) : (
                      filteredMembers.map((m) => (
                        <button
                          key={`${m.source}-${m.id}`}
                          type="button"
                          onClick={() => {
                            setSelectedMember(m);
                            setMemberSearch("");
                          }}
                          className="w-full text-left p-2 text-sm hover:bg-muted transition-colors"
                        >
                          {m.name}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Articles */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Artículos</label>
            {articlesLoading ? (
              <p className="text-xs text-muted-foreground">Cargando...</p>
            ) : articles.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No hay artículos creados. Usá "Gestionar artículos" para agregarlos.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {articles.map((a) => {
                  const inCart = cart.some((c) => c.article.id === a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => addArticle(a)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm border transition-colors",
                        inCart
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background border-border hover:bg-muted"
                      )}
                    >
                      {a.name} · ${Number(a.price).toLocaleString()}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cart */}
          {cart.length > 0 && (
            <div className="space-y-2 border rounded-md p-3">
              {cart.map((item) => (
                <div key={item.article.id} className="space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium flex-1 truncate">
                      {item.article.name}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => removeArticle(item.article.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.article.id, item.quantity - 1)}
                      >
                        <Minus className="h-3 w-3" />
                      </Button>
                      <span className="text-sm w-6 text-center">{item.quantity}</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => updateQuantity(item.article.id, item.quantity + 1)}
                      >
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice === 0 ? "" : item.unitPrice}
                      onChange={(e) =>
                        updateUnitPrice(item.article.id, Number(e.target.value) || 0)
                      }
                      className="h-8 flex-1"
                      placeholder="Precio"
                    />
                    <span className="text-sm font-medium whitespace-nowrap">
                      ${(item.unitPrice * item.quantity).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex justify-between pt-2 border-t font-semibold">
                <span>Total</span>
                <span>${total.toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* Date */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Fecha de pago</label>
            <DatePicker
              value={paidAt}
              onChange={setPaidAt}
              maxYear={new Date().getFullYear()}
              placeholder="Seleccionar fecha"
            />
          </div>

          {/* Payment method */}
          <PaymentMethodSelector
            methods={methods}
            selectedId={selectedMethodId}
            onSelect={setSelectedMethodId}
            onManageClick={() => setIsManagingMethods(true)}
            isLoading={methodsLoading}
          />

          <Button
            type="button"
            className="w-full"
            disabled={
              cart.length === 0 ||
              total <= 0 ||
              !selectedMethodId ||
              isSubmitting
            }
            onClick={handleSubmit}
          >
            {isSubmitting ? "Registrando..." : "Registrar venta"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
